import { ApiClientError } from "@/lib/api";
import type { FieldDefinition } from "./types";

/**
 * #1099 #1047: what a refused save or validation of the connection editor says, and where.
 *
 * The server's `details` are `{ field, issue }` pairs keyed by the body's own names
 * (`displayName`, `fields.<id>`, `entities.<table>.targetName`), and some of them are only
 * identifiers for support (a 409 `TABLE_NAME_TAKEN`'s ids, a `STALE_UPDATE`'s stamp). The panel
 * above the save bar names a field only by the label on screen; anything it cannot name that way
 * stays behind the reference id. The Display name and Name refusals are also said at their own
 * field (`refusalFieldErrors`).
 */

/** The connection fields a refusal can mark in place. */
export interface RefusalFieldErrors {
  displayName?: string;
  name?: string;
  description?: string;
}

export const NO_REFUSAL_FIELD_ERRORS: RefusalFieldErrors = {};

/** The server's reasons for the Display name and the Name, to show at those fields. */
export function refusalFieldErrors(error: unknown): RefusalFieldErrors {
  if (!(error instanceof ApiClientError)) return NO_REFUSAL_FIELD_ERRORS;
  const found: RefusalFieldErrors = {};
  for (const detail of error.details) {
    if (detail.field === "displayName" && !found.displayName) found.displayName = detail.issue;
    if (detail.field === "name" && !found.name) found.name = detail.issue;
    if (detail.field === "description" && !found.description) found.description = detail.issue;
  }
  return found;
}

/** The labels the panel names fields by: the editor's own and the connector's fields. */
export interface RefusalLabels {
  fieldDefinitions: readonly Pick<FieldDefinition, "id" | "name">[];
  /** The connector's word for one entity, "Table" for most. */
  entitySingular: string;
  /** And for several, "Tables". */
  entityPlural: string;
  /**
   * What the refused request sent, in order. A body check (zod) names an array item by its index
   * (`fields.2.value`, `entities.3.targetName`), and only the request knows which field or table
   * that index was. Absent, an index path is not labelled.
   */
  sent?: RefusalSentBody;
}

/** The ids of the fields and the names of the entities a request sent, in its own order. */
export interface RefusalSentBody {
  fieldIds?: readonly string[];
  entityNames?: readonly string[];
}

/** The panel's line when a body check refused fields it cannot name on screen. */
export const UNLABELLED_FIELD_LINE = "One of the fields is not valid.";

const CONNECTION_LABELS: Record<string, string> = {
  displayName: "Display name",
  name: "Name",
  description: "Description",
  imageTag: "Version",
  confirmDestination: "Destination",
};

function capitalised(text: string): string {
  return text ? `${text.charAt(0).toUpperCase()}${text.slice(1)}` : text;
}

const INDEX = /^\d+$/;

/** A connector field's label, by its id or (a body check's path) by its index in the request. */
function connectorFieldLabel(path: string, labels: RefusalLabels): string | null {
  const byId = (id: string | undefined) =>
    id === undefined
      ? null
      : (labels.fieldDefinitions.find((definition) => definition.id === id)?.name ?? null);
  // The service names a field by its id (`fields.<id>`), and an id may hold a dot.
  const named = byId(path);
  if (named) return named;
  const [index, ...rest] = path.split(".");
  if (index !== undefined && INDEX.test(index) && rest.length <= 1) {
    return byId(labels.sent?.fieldIds?.[Number(index)]);
  }
  return null;
}

/** An entity's label, "Table dbo.orders", by its name or by its index in the request. */
function entityLabel(path: string, labels: RefusalLabels): string | null {
  const word = capitalised(labels.entitySingular);
  const indexed = /^(\d+)(?:\.[A-Za-z]+)?$/.exec(path)?.[1];
  if (indexed !== undefined) {
    const name = labels.sent?.entityNames?.[Number(indexed)];
    return name ? `${word} ${name}` : null;
  }
  // The service names a table by its name (`entities.<name>.targetName`), never an index.
  const table = /^(.+)\.targetName$/.exec(path)?.[1];
  return table ? `${word} ${table}` : null;
}

/** The on-screen label of a detail's field, or null when the field is not one a person sees. */
function labelOf(field: string, labels: RefusalLabels): string | null {
  const own = CONNECTION_LABELS[field];
  if (own) return own;
  if (field === "entities") return capitalised(labels.entityPlural);
  const connectorField = /^fields\.(.+)$/.exec(field)?.[1];
  if (connectorField) return connectorFieldLabel(connectorField, labels);
  const entity = /^entities\.(.+)$/.exec(field)?.[1];
  if (entity) return entityLabel(entity, labels);
  return null;
}

/**
 * The panel's lines under the sentence: each reason with the label of its field ("Display name:
 * A connection needs a name."), a reason that names no field as it is, and none for a field the
 * screen does not show, which stays behind the reference id.
 */
export function refusalPanelLines(error: unknown, labels: RefusalLabels): string[] {
  if (!(error instanceof ApiClientError)) return [];
  // The table rows are marked with the sentence; the details are the two tables' ids (SEC-17).
  if (error.code === "TABLE_NAME_TAKEN") return [];
  const lines: string[] = [];
  for (const detail of error.details) {
    let line: string | null;
    if (!detail.field) {
      line = detail.issue;
    } else {
      const label = labelOf(detail.field, labels);
      line = label ? `${label}: ${detail.issue}` : null;
    }
    // A 409 that names the field repeats the sentence above it; once is enough.
    if (line && detail.issue !== error.message && !lines.includes(line)) lines.push(line);
  }
  // A body check that named only fields the screen cannot label still says that a field is the
  // cause, so the panel is never the catalog sentence alone ("Check the highlighted fields").
  if (
    lines.length === 0 &&
    error.code === "VALIDATION_FAILED" &&
    error.details.some((detail) => detail.field)
  ) {
    lines.push(UNLABELLED_FIELD_LINE);
  }
  return lines;
}
